import type { ReactNode } from 'react';
import { Cluster, Inline } from './Layout';

// ------------------------------------------------------------------
// WorkspaceHeader / ContextHeader / SectionHeader — story 2-147sb (P10 §2.6
// header anatomy, kinds #3/#4/#5; DOD). `PageHeader.tsx` (kind #2) already
// shipped real in story 2-127 — this file is the other three kinds `2-147e`
// NAMED but never built as standalone, reusable components:
//
//   - WorkspaceHeader (kind #3) was previously only a SLOT CONTRACT, composed
//     inline per consumer (`TemplateEditorPage.tsx`'s own header row,
//     `CapturePage.tsx`, and this gallery's own `WorkspaceHeaderDemo`) — this
//     story extracts that shape into a real primitive for the first time.
//   - ContextHeader (kind #4) already existed, inline, inside
//     `RailPanel.tsx`'s own render body (title + trailing action row) —
//     `2-147e`'s own prose called this kind "already real and already
//     consistent," citing RailPanel as the worked example rather than a
//     component to build. This story extracts that same block into a named,
//     independently reusable component and has `RailPanel` compose it
//     instead of duplicating the markup — a REFINEMENT of existing shape,
//     not a fresh build (see RailPanel.tsx's own updated header comment).
//   - SectionHeader (kind #5) formalizes the bare `<h2>` convention P10 §2.6
///    already named ("text-lg font-semibold text-foreground... no card/slot
//     contract of its own"). `Layout.tsx`'s `Section` primitive (2-147sa)
//     already renders an inline header block with this exact shape —
//     DELIBERATELY left as its own inline implementation rather than
//     refactored to import `SectionHeader` from this file, because `Layout.tsx`
//     is the primitive layer `Headers.tsx` itself depends on (this file
//     imports `Cluster` from it) — having `Layout.tsx` import back from
//     `Headers.tsx` would be a circular dependency between the two files.
//     Both implementations render byte-identical markup by construction
//     (`SectionHeader` is the extracted/named form of the same block) — a
//     recorded, deliberate duplication of ~6 lines to keep the dependency
//     graph one-directional, not an oversight.
//
// All three compose `2-147sa`'s `Cluster`/`Inline` layout primitives
// internally, matching this story's own "built from 2-147sa's primitives,
// not bespoke divs" requirement — carried through to header components too,
// not just the six page layouts.
// ------------------------------------------------------------------

// ------------------------------------------------------------------
// WorkspaceHeader — identity (back-link + title) plus two independently-
// wrapping control clusters (tools; save/lifecycle), separated by a
// hairline divider at `sm`+ — the same shape `TemplateEditorPage.tsx`'s own
// header row and this gallery's `WorkspaceHeaderDemo` already hand-roll.
// Presentational only, matching `PageHeader`'s own contract: `back`/`tools`/
// `lifecycle` are slots the caller fills with existing DS primitives
// (`Button`, `SegmentedControl`, `SaveStateIndicator`, `HeaderActions`) —
// WorkspaceHeader never decides what a tool or a save action is.
// ------------------------------------------------------------------
export interface WorkspaceHeaderProps {
  /** Back-link control, e.g. `<Button variant="secondary" size="sm">← Templates</Button>`. Omit for a workspace with no parent list to return to. */
  back?: ReactNode;
  title: string;
  /**
   * Status chips beside the title — same slot/placement convention as
   * `PageHeader`'s own `chips` prop (story 2-127), extended here (story
   * 2-147wd, direct live user feedback: "status should go beside the
   * title following our design system"). Compose `Badge`/`StatusChip`.
   * Omit for a workspace with no record-level status to show yet (e.g. a
   * brand-new, unsaved template).
   */
  chips?: ReactNode;
  /** Tool cluster — view-mode switch (`SegmentedControl`), undo/redo, etc. */
  tools?: ReactNode;
  /** Save/lifecycle cluster — draft/status badge, `SaveStateIndicator`, primary Save action. Compose `HeaderActions` (2-147r) for the action(s) inside this slot rather than a raw `Button`. */
  lifecycle?: ReactNode;
  /** Defaults to 1 — a real workspace page's own `<h1>` (same convention `PageHeader` states: this primitive typically replaces a page's hand-rolled `<h1>`). Pass 2 where a second real `<h1>` must not appear (e.g. this gallery's own demo). */
  headingLevel?: 1 | 2;
  className?: string;
  'data-testid'?: string;
}

export function WorkspaceHeader({
  back,
  title,
  chips,
  tools,
  lifecycle,
  headingLevel = 1,
  className = '',
  'data-testid': testId,
}: WorkspaceHeaderProps): JSX.Element {
  const Heading = headingLevel === 1 ? 'h1' : 'h2';
  const hasControls = Boolean(tools || lifecycle);
  return (
    <header className={`rounded-hairline border border-border bg-card p-4 ${className}`} data-testid={testId}>
      <Cluster justify="between" align="start" gap={3}>
        {/* `chips` (story 2-147wd) is a WRAP-CAPABLE group beside back+title
            — deliberately NOT inside the `back`+`title` `Inline` pair
            itself (`Inline` is `flex-nowrap` by contract, Layout.tsx's own
            header comment: "a single unbreakable horizontal unit"). A
            narrow-viewport regression this story's own e2e caught live: a
            status chip (plus, on a template with unpublished changes, a
            second qualifier chip) sharing that nowrap pair's row forced the
            title's own `min-w-0 flex-1` to shrink to a sliver instead of
            the chip ever moving. This outer `Cluster` (`flex-wrap`, matching
            `PageHeader.tsx`'s own chips-beside-title precedent) lets `chips`
            drop to its own line at a narrow width while back+title keep
            their full available width on the first line — the identity
            block still takes ONE `basis-full` item overall (`sm`
            breakpoint prop below, unchanged), it just now has two
            independently-wrapping rows inside it instead of one rigid one. */}
        <Cluster gap={3} align="center" className="min-w-0 basis-full sm:basis-auto sm:flex-1">
          <Inline gap={3} align="center" className="min-w-0 flex-1">
            {back}
            <Heading className="min-w-0 flex-1 truncate text-base font-semibold text-foreground">{title}</Heading>
          </Inline>
          {/* `w-full sm:w-auto`, not just `flex-wrap` on the parent
              `Cluster`: a `flex-wrap` row still tries to SHRINK a flexible
              sibling (this `Inline`, `min-w-0 flex-1`) to fit both items on
              one line before it ever wraps — and `min-w-0` on the Heading
              makes that shrink-to-near-nothing legal, so the two techniques
              fight and the title loses first. `w-full` below `sm` makes
              this item claim the WHOLE row width on its own — nothing can
              share a 100%-width flex item's line — which forces it onto a
              genuinely separate line deterministically, not by winning a
              shrink race. `sm:w-auto` reverts to the natural shared-row
              width at `sm`+, unchanged from desktop's own layout. */}
          {chips && <div className="flex w-full shrink-0 flex-wrap items-center gap-1.5 sm:w-auto">{chips}</div>}
        </Cluster>
        {hasControls && (
          <Cluster gap={3} align="center">
            {tools}
            {tools && lifecycle && <div className="hidden h-6 w-px shrink-0 bg-border/60 sm:block" aria-hidden="true" />}
            {lifecycle}
          </Cluster>
        )}
      </Cluster>
    </header>
  );
}

// ------------------------------------------------------------------
// ContextHeader — a rail panel's own title + optional trailing action.
// Extracted from `RailPanel.tsx`'s own render body (unchanged shape,
// unchanged classes) so the "context header" kind is independently
// reusable/citable rather than only living inline inside one consumer.
// ------------------------------------------------------------------
export interface ContextHeaderProps {
  title: string;
  action?: ReactNode;
  headingLevel?: 2 | 3 | 4;
  className?: string;
  'data-testid'?: string;
}

export function ContextHeader({
  title,
  action,
  headingLevel = 3,
  className = '',
  'data-testid': testId,
}: ContextHeaderProps): JSX.Element {
  const Heading = `h${headingLevel}` as 'h2' | 'h3' | 'h4';
  return (
    <Cluster justify="between" align="center" gap={2} className={className} {...(testId ? { 'data-testid': testId } : {})}>
      <Heading className="min-w-0 text-sm font-semibold text-foreground">{title}</Heading>
      {action && <div className="shrink-0">{action}</div>}
    </Cluster>
  );
}

// ------------------------------------------------------------------
// SectionHeader — an in-page subheading: title + description + trailing
// actions, no card/slot contract of its own (P10 §2.6 kind #5). Same shape
// `Layout.tsx`'s `Section` primitive already renders inline — see this
// file's own header comment for why that block is not refactored to import
// this component (avoiding a circular Layout.tsx ↔ Headers.tsx dependency).
// ------------------------------------------------------------------
export interface SectionHeaderProps {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
  'data-testid'?: string;
}

export function SectionHeader({
  title,
  description,
  actions,
  className = '',
  'data-testid': testId,
}: SectionHeaderProps): JSX.Element | null {
  if (title == null && description == null && actions == null) return null;
  return (
    <Cluster justify="between" align="start" gap={4} className={className} {...(testId ? { 'data-testid': testId } : {})}>
      <div>
        {title != null && <h2 className="text-lg font-semibold text-foreground">{title}</h2>}
        {description != null && <p className="text-sm text-muted-foreground">{description}</p>}
      </div>
      {actions != null && <Cluster gap={2}>{actions}</Cluster>}
    </Cluster>
  );
}
